import clsx from 'clsx';

import * as api from '@gitbook/api-client';
import { Button, Description, Group, Heading, ProgressiveImage, toast } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { siteMultipleClients } from '../../../assets';
import { PaidFeatureIndicator } from '../../../Components';
import {
    apiClient,
    useSiteContext,
    useCurrentOrganization,
    useGetUpgradeAction,
} from '../../../state';
import { createDocumentationURL } from '../../../utils';

/**
 * Intro for the site adaptive content feature.
 */
export function AdaptiveContentFeatureIntro(props: {
    orientation?: 'horizontal' | 'vertical';
    onFeatureEnabled?: () => void;
}) {
    const { orientation = 'horizontal', onFeatureEnabled } = props;
    const organization = useCurrentOrganization();
    const { site, organizationId } = useSiteContext();
    const upgrade = useGetUpgradeAction({
        organization,
        site,
        featureKey: api.SiteFeatureKey.SitesAdaptiveContent,
    });
    const [_, wrapLoading] = useLoadingState();

    const onEnable = async () => {
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await apiClient.sites.updateSiteById(organizationId, site.id, {
                    adaptiveContent: {
                        enabled: true,
                    },
                });
                toast.success('Adaptive content enabled');
            }, 'Error while enabling adaptive content');
        });

        onFeatureEnabled?.();
    };

    return (
        <div
            className={clsx(
                'grid gap-4',
                orientation === 'horizontal'
                    ? 'grid-cols-1 lg:grid-cols-2'
                    : 'grid-rows-1 lg:grid-rows-2'
            )}
        >
            <div
                className={clsx('flex flex-col justify-center gap-4', {
                    'order-2': orientation === 'vertical',
                    'items-center': orientation === 'vertical',
                })}
            >
                <Group>
                    <Heading>Adapt your docs to who's reading them</Heading>
                    <PaidFeatureIndicator
                        upgrade={upgrade}
                        featureKey={api.SiteFeatureKey.SitesAdaptiveContent}
                    />
                </Group>
                <Description>
                    Securely pass data to GitBook to tailor your visitors’ experience. Set up
                    conditions to show or hide parts of your docs. Make it personal by replacing
                    docs content with your visitors’ data.
                </Description>
                <div
                    className={clsx(
                        'flex flex-row gap-2',
                        orientation === 'vertical' ? 'm-auto' : undefined
                    )}
                >
                    <Button
                        label="Enable adaptive content"
                        onPress={() => {
                            if (upgrade) {
                                return upgrade.onPress();
                            }

                            return onEnable();
                        }}
                    />
                    <Button
                        kind={orientation === 'horizontal' ? 'blank' : 'secondary'}
                        target="_blank"
                        label="Learn more"
                        href={createDocumentationURL('publishing-documentation/adaptive-content')}
                    />
                </div>
            </div>
            <div
                className={clsx({
                    'order-1': orientation === 'vertical',
                })}
            >
                <ProgressiveImage
                    src={siteMultipleClients}
                    width="100%"
                    height="auto"
                    style={{ aspectRatio: '450 / 153' }}
                />
            </div>
        </div>
    );
}
